// ===== Drawing: soft, colourful 3D-like style =====
// General drawing: background, fixed blocks, balls, player figure and tool icons.
// The core's parts, machines and goals are drawn by the kit (src/kit/); the basic shapes are in draw-helpers.ts.
import { Registry } from './registry';
import { sceneSvg, hasScene } from './scenes-lib';
import { C, rr, lg, block, shadowOn, shadowOff } from './draw-helpers';
import type { Ctx2D, GameBody, Level, WorldDef, BgApi } from './types';
import KITCHEN_IMG from '../images/kitchen.jpg';

export const GFX = (() => {
  // ---------- background (cached per level) ----------
  const bgCache = new Map<Level, HTMLCanvasElement>();
  // Theme picture (Morgenmadskaos / Breakfast Chaos)
  const bgImg = KITCHEN_IMG ? new Image() : null;
  let bgVer = 0;
  if (bgImg) { bgImg.onload = () => { bgCache.clear(); bgVer++; }; bgImg.src = KITCHEN_IMG; }
  // Theme worlds: background from the world's theme picture (src/worlds/<key>/scene.ts) + the world's own drawing
  const sceneImgs: Record<string, HTMLImageElement> = {};
  function sceneImg(k: string) {
    if (sceneImgs[k] || !hasScene(k)) return sceneImgs[k];
    const im = new Image(); sceneImgs[k] = im;
    im.onload = () => { bgCache.clear(); bgVer++; };
    im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(sceneSvg(k, true));
    return im;
  }
  let curW: string | null = null;
  const WD = (): WorldDef | null => (curW && Registry.worlds[curW]) || null;
  function setWorld(k: string | null | undefined) { curW = k || null; }
  function background(level: Level, W: number, H: number): HTMLCanvasElement {
    const hit = bgCache.get(level); if (hit) return hit;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d')!;
    const wd = level.wk && level.wk !== 'breakfast' ? Registry.worlds[level.wk] : null;
    if (wd) {
      const api: BgApi = { C, rr, lg, block, shadowOn, shadowOff, W, H };
      if (wd.bg) { wd.bg(ctx, W, H, level, api); bgCache.set(level, cv); return cv; }
      const im = sceneImg(level.wk!);
      if (im && im.complete && im.naturalWidth) {
        ctx.drawImage(im, 0, 0, W, H);
        ctx.fillStyle = wd.wash || 'rgba(255,248,236,.5)'; ctx.fillRect(0, 0, W, H);
        if (wd.bgExtra) wd.bgExtra(ctx, W, H, level, api);
        bgCache.set(level, cv);
      } else { ctx.fillStyle = lg(ctx, 0, H, C.wall1, C.wall2); ctx.fillRect(0, 0, W, H); if (wd.bgExtra) wd.bgExtra(ctx, W, H, level, api); }
      return cv;
    }
    if (bgImg && bgImg.complete && bgImg.naturalWidth && (level.bg || 'kitchen') === 'kitchen') {
      // light "veil", so the game's parts stand out clearly
      ctx.drawImage(bgImg, 0, 0, W, H);
      ctx.fillStyle = 'rgba(255,246,230,.5)'; ctx.fillRect(0, 0, W, H);
      const v = ctx.createRadialGradient(W / 2, H / 2, 200, W / 2, H / 2, 760); v.addColorStop(0, 'rgba(255,250,240,.25)'); v.addColorStop(1, 'rgba(255,250,240,0)');
      ctx.fillStyle = v; ctx.fillRect(0, 0, W, H);
      bgCache.set(level, cv);
      return cv;
    }
    ctx.fillStyle = lg(ctx, 0, H, C.wall1, C.wall2); ctx.fillRect(0, 0, W, H);
    // tiles at the bottom
    ctx.save(); ctx.globalAlpha = 0.7;
    for (let y = 520; y < H; y += 46) for (let x = ((y / 46) % 2) * 23 - 23; x < W; x += 46) { rr(ctx, x + 3, y + 3, 40, 40, 8); ctx.fillStyle = C.tile; ctx.fill(); }
    ctx.restore();
    // wide stripe
    ctx.fillStyle = 'rgba(255,170,120,.25)'; ctx.fillRect(0, 505, W, 10);
    // window with sky
    const win = level.win as number[] | undefined, clock = level.clock as number[] | undefined;
    const wx = win ? win[0] : 980, wy = win ? win[1] : 40;
    ctx.save(); shadowOn(ctx, 10, 4); rr(ctx, wx, wy, 170, 130, 18); ctx.fillStyle = '#fff'; ctx.fill(); shadowOff(ctx);
    rr(ctx, wx + 10, wy + 10, 150, 110, 12); ctx.fillStyle = lg(ctx, wy, wy + 120, '#7FD3FF', '#CFF0FF'); ctx.fill();
    ctx.beginPath(); ctx.arc(wx + 120, wy + 42, 18, 0, 7); ctx.fillStyle = '#FFE36B'; ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,.9)';
    [[wx + 40, wy + 70, 16], [wx + 60, wy + 64, 20], [wx + 82, wy + 72, 14]].forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill(); });
    ctx.fillStyle = '#fff'; ctx.fillRect(wx + 83, wy + 10, 5, 110); ctx.fillRect(wx + 10, wy + 63, 150, 5);
    ctx.restore();
    // wall clock
    const cx = clock ? clock[0] : 330, cy = clock ? clock[1] : 62;
    ctx.save(); shadowOn(ctx, 8, 3); ctx.beginPath(); ctx.arc(cx, cy, 34, 0, 7); ctx.fillStyle = '#FF9F6B'; ctx.fill(); shadowOff(ctx);
    ctx.beginPath(); ctx.arc(cx, cy, 26, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.strokeStyle = C.ink; ctx.lineWidth = 4; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx, cy - 17); ctx.moveTo(cx, cy); ctx.lineTo(cx + 12, cy + 4); ctx.stroke();
    ctx.restore();
    bgCache.set(level, cv);
    return cv;
  }

  // ---------- fixed blocks ----------
  // The world's skin (b.wk), otherwise the kit's (src/kit/skins.ts), otherwise purple
  function staticBody(ctx: Ctx2D, b: GameBody) {
    const { x, y } = b.position, a = b.angle, w = b.dw!, h = b.dh!;
    const wd = b.wk ? Registry.worlds[b.wk] : null, k = b.skin || '';
    const sk = (wd && wd.skins && wd.skins[k]) || Registry.skins[k];
    if (sk) { if (typeof sk === 'function') sk(ctx, b, GFXO); else { const s = sk as { c1: string; c2: string; r?: number }; block(ctx, x, y, w, h, a, s.c1, s.c2, s.r != null ? s.r : Math.min(10, h / 2)); } return; }
    block(ctx, x, y, w, h, a, C.lilac1, C.lilac2, Math.min(12, w / 2));
  }

  // ---------- balls with faces ----------
  function ball(ctx: Ctx2D, b: GameBody, kind: string, t: number) {
    const wd = WD();
    if (wd && wd.ball && !b.plain) { wd.ball(ctx, b, kind, t, GFXO); return; }
    const { x, y } = b.position, r = b.circleRadius!;
    const v = b.velocity, sp = Math.hypot(v.x, v.y);
    ctx.save(); ctx.translate(x, y);
    shadowOn(ctx, 8, 5);
    const g = ctx.createRadialGradient(-r * .35, -r * .4, r * .15, 0, 0, r * 1.05);
    if (kind === 'hero') { g.addColorStop(0, b.painted ? '#FFB3DA' : '#D8FFA8'); g.addColorStop(0.5, b.painted ? '#FF73B9' : C.pea1); g.addColorStop(1, b.painted ? '#D4388A' : C.pea2); }
    else { g.addColorStop(0, '#D6DBFF'); g.addColorStop(0.5, C.berry1); g.addColorStop(1, C.berry2); }
    ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = g; ctx.fill(); shadowOff(ctx);
    // the face rolls round with the ball
    ctx.save(); ctx.rotate(b.roll || 0);
    if (kind === 'helper') { ctx.fillStyle = '#3FA535'; ctx.beginPath(); ctx.ellipse(2, -r - 2, 6, 3, -0.4, 0, 7); ctx.fill(); }
    // face
    const lx = Math.max(-2.5, Math.min(2.5, v.x * 0.5)), ly = Math.max(-2.5, Math.min(2.5, v.y * 0.5));
    const scared = v.y > 9, fast = sp > 8.5;
    const ex = r * 0.34, ey = -r * 0.12, er = r * 0.27;
    for (const s of [-1, 1]) {
      ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
      if ((b.dizzy || 0) > 0) {
        ctx.strokeStyle = C.ink; ctx.lineWidth = 1.6; ctx.beginPath();
        for (let i = 0; i < 12; i++) { const tt = i / 12 * Math.PI * 3 + t * 0.3, rr2 = i / 12 * er * 0.8; ctx.lineTo(s * ex + Math.cos(tt) * rr2, ey + Math.sin(tt) * rr2); }
        ctx.stroke();
      } else {
        ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared ? 0.35 : 0.55), 0, 7); ctx.fillStyle = C.ink; ctx.fill();
      }
      if (scared) { ctx.strokeStyle = C.ink; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.moveTo(s * ex - 4, ey - er - 3 + s * -1.5); ctx.lineTo(s * ex + 4, ey - er - 3 + s * 1.5); ctx.stroke(); }
    }
    ctx.fillStyle = C.ink; ctx.strokeStyle = C.ink; ctx.lineWidth = 2; ctx.lineCap = 'round';
    if (b.sad) { ctx.beginPath(); ctx.arc(0, r * 0.62, r * 0.26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
    else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * 0.42, r * 0.17, r * 0.22, 0, 0, 7); ctx.fill(); }
    else { ctx.beginPath(); ctx.arc(0, r * 0.25, r * 0.28, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
    // cheeks
    ctx.fillStyle = 'rgba(255,120,150,.45)';
    [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.58, r * 0.22, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
    ctx.restore();
    // gloss (the light stays still)
    ctx.beginPath(); ctx.ellipse(-r * .38, -r * .45, r * .28, r * .18, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
    ctx.restore();
  }

  // ---------- Player figure: animal + jumper + shoes ----------
  const ANIMALS: Record<string, { c1: string; c2: string }> = {
    frog:  { c1: '#9BE15D', c2: '#4FB83A' }, fox: { c1: '#FFB067', c2: '#F07A2A' }, panda: { c1: '#FFFFFF', c2: '#DCD8E6' },
    tiger: { c1: '#FFC75A', c2: '#F29A16' }, cat: { c1: '#D6D0E2', c2: '#A79FBC' }, bunny: { c1: '#FFF1F5', c2: '#F2C9D8' },
  };
  function head(ctx: Ctx2D, an: string, x: number, y: number, R: number) {
    const A = ANIMALS[an] || ANIMALS.frog;
    const fill = (c1: string, c2: string, y0: number, y1: number) => lg(ctx, y0, y1, c1, c2);
    ctx.save(); ctx.translate(x, y);
    // ears
    ctx.fillStyle = fill(A.c1, A.c2, -R, R);
    if (an === 'fox' || an === 'cat' || an === 'tiger') {
      for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * R * .25, -R * .8); ctx.lineTo(s * R * .95, -R * 1.25); ctx.lineTo(s * R * .9, -R * .3); ctx.closePath(); ctx.fill();
        ctx.fillStyle = an === 'fox' ? '#3B2A5C' : '#FFB3C7'; ctx.beginPath(); ctx.moveTo(s * R * .42, -R * .78); ctx.lineTo(s * R * .86, -R * 1.08); ctx.lineTo(s * R * .8, -R * .5); ctx.closePath(); ctx.fill(); ctx.fillStyle = fill(A.c1, A.c2, -R, R); }
    } else if (an === 'bunny') {
      for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * R * .38, -R * 1.25, R * .22, R * .62, s * .15, 0, 7); ctx.fill();
        ctx.fillStyle = '#FFB3C7'; ctx.beginPath(); ctx.ellipse(s * R * .38, -R * 1.22, R * .1, R * .45, s * .15, 0, 7); ctx.fill(); ctx.fillStyle = fill(A.c1, A.c2, -R, R); }
    } else if (an === 'panda') {
      ctx.fillStyle = '#3B2A5C'; for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * R * .72, -R * .72, R * .3, 0, 7); ctx.fill(); }
    } else if (an === 'frog') {
      for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * R * .45, -R * .78, R * .36, 0, 7); ctx.fill(); }
    }
    // head
    shadowOn(ctx, 10, 4);
    ctx.beginPath(); ctx.ellipse(0, 0, R, R * .92, 0, 0, 7); ctx.fillStyle = fill(A.c1, A.c2, -R, R); ctx.fill(); shadowOff(ctx);
    // patterns
    if (an === 'tiger') { ctx.strokeStyle = '#8A4A12'; ctx.lineWidth = R * .09; ctx.lineCap = 'round';
      for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * R * .95, -R * .1); ctx.lineTo(s * R * .6, -R * .02); ctx.stroke(); ctx.beginPath(); ctx.moveTo(s * R * .9, R * .22); ctx.lineTo(s * R * .6, R * .2); ctx.stroke(); }
      ctx.beginPath(); ctx.moveTo(0, -R * .88); ctx.lineTo(0, -R * .6); ctx.stroke(); }
    if (an === 'fox' || an === 'cat' || an === 'tiger' || an === 'bunny') { ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.ellipse(0, R * .35, R * .55, R * .4, 0, 0, 7); ctx.fill(); }
    if (an === 'panda') { ctx.fillStyle = '#3B2A5C'; for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * R * .36, -R * .08, R * .22, R * .3, s * .5, 0, 7); ctx.fill(); } }
    // eyes
    const ey = an === 'frog' ? -R * .78 : -R * .08, ex = an === 'frog' ? R * .45 : R * .36, er = an === 'frog' ? R * .22 : R * .16;
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
      ctx.beginPath(); ctx.arc(s * ex + R * .03, ey + R * .02, er * .55, 0, 7); ctx.fillStyle = '#3B2A5C'; ctx.fill();
      ctx.beginPath(); ctx.arc(s * ex + R * .07, ey - R * .04, er * .2, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); }
    // nose and mouth
    ctx.fillStyle = an === 'bunny' || an === 'cat' ? '#FF8FB0' : '#3B2A5C';
    if (an !== 'frog') { ctx.beginPath(); ctx.ellipse(0, R * .2, R * .1, R * .07, 0, 0, 7); ctx.fill(); }
    ctx.strokeStyle = '#3B2A5C'; ctx.lineWidth = R * .07; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.arc(0, R * .22, R * (an === 'frog' ? .42 : .22), .2 * Math.PI, .8 * Math.PI); ctx.stroke();
    ctx.fillStyle = 'rgba(255,120,150,.45)'; for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * R * .62, R * .28, R * .14, R * .09, 0, 0, 7); ctx.fill(); }
    ctx.restore();
  }
  // Whole figure. (x, y) = midway between the feet, h = total height
  function char(ctx: Ctx2D, ch: { animal: string; shirt: string; shoes: string }, x: number, y: number, h: number) {
    const k = h / 300, A = ANIMALS[ch.animal] || ANIMALS.frog;
    ctx.save(); ctx.translate(x, y); ctx.scale(k, k);
    // shadow on the floor
    ctx.fillStyle = 'rgba(59,42,92,.15)'; ctx.beginPath(); ctx.ellipse(0, 0, 70, 12, 0, 0, 7); ctx.fill();
    // legs
    ctx.fillStyle = lg(ctx, -80, 0, A.c1, A.c2);
    for (const s of [-1, 1]) { rr(ctx, s * 22 - 11, -78, 22, 64, 10); ctx.fill(); }
    // shoes
    for (const s of [-1, 1]) {
      ctx.beginPath(); ctx.ellipse(s * 26, -12, 26, 14, 0, 0, 7); ctx.fillStyle = ch.shoes; ctx.fill();
      ctx.beginPath(); ctx.ellipse(s * 26, -2, 26, 5, 0, 0, Math.PI); ctx.fillStyle = '#fff'; ctx.fill();
      ctx.beginPath(); ctx.ellipse(s * 20, -18, 8, 4, -0.3, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.5)'; ctx.fill();
    }
    // arms
    for (const s of [-1, 1]) {
      ctx.save(); ctx.translate(s * 50, -150); ctx.rotate(s * -0.35);
      rr(ctx, -13, 0, 26, 62, 13); ctx.fillStyle = ch.shirt; ctx.fill(); ctx.fillStyle = 'rgba(0,0,0,.08)'; ctx.fill();
      ctx.beginPath(); ctx.arc(0, 66, 13, 0, 7); ctx.fillStyle = A.c1; ctx.fill();
      ctx.restore();
    }
    // jumper
    shadowOn(ctx, 10, 4);
    ctx.beginPath(); ctx.moveTo(-46, -168); ctx.quadraticCurveTo(-58, -110, -44, -70); ctx.lineTo(44, -70); ctx.quadraticCurveTo(58, -110, 46, -168); ctx.quadraticCurveTo(0, -182, -46, -168); ctx.closePath();
    ctx.fillStyle = ch.shirt; ctx.fill(); shadowOff(ctx);
    ctx.fillStyle = 'rgba(255,255,255,.25)'; rr(ctx, -40, -160, 18, 70, 9); ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,.12)'; rr(ctx, -46, -82, 92, 12, 6); ctx.fill();
    // star on the tummy
    ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.beginPath();
    for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5, rr2 = i % 2 ? 7 : 16; ctx.lineTo(Math.cos(a) * rr2, -120 + Math.sin(a) * rr2); }
    ctx.fill();
    head(ctx, ch.animal, 0, -222, 58);
    ctx.restore();
  }

  // Toolbox icon: the world's icon for a core part (world.icons), otherwise the part's own
  function icon(cv: HTMLCanvasElement, t: string) {
    const ctx = cv.getContext('2d')!, s = cv.width;
    ctx.clearRect(0, 0, s, s);
    ctx.save(); ctx.translate(s / 2, s / 2); const k = s / 120; ctx.scale(k, k);
    const pd = Registry.parts[t], wd = WD(), own = wd && wd.icons && (wd.icons as Record<string, ((c: Ctx2D, g: typeof GFXO) => void) | undefined>)[t];
    if (own) own(ctx, GFXO);
    else if (pd && pd.icon) pd.icon(ctx, GFXO);
    ctx.restore();
  }

  const GFXO = {
    ANIMALS, head, char, get bgVer() { return bgVer; }, setWorld, get world() { return curW; }, worldDef: WD, sceneImg, bgImg,
    C, rr, lg, block, background, staticBody, ball, icon, shadowOn, shadowOff,
  };
  return GFXO;
})();
